import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Bell, Loader2, Send, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/config/api';

export default function AdminPushBroadcast() {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('');
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null); // server_fcm diagnostic snapshot
  const [lastResult, setLastResult] = useState(null);
  const [history, setHistory] = useState([]);

  const loadStatus = async () => {
    try {
      const res = await fetch(`${API}/notifications/fcm/status`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setStatus(data.server_fcm || null);
      }
    } catch (_) { /* ignore */ }
  };

  const loadHistory = async () => {
    try {
      const res = await fetch(`${API}/admin/notifications/broadcasts?limit=10`, { credentials: 'include' });
      if (res.ok) setHistory(await res.json());
    } catch (_) { /* ignore */ }
  };

  useEffect(() => {
    loadStatus();
    loadHistory();
  }, []);

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error('Title and body are required');
      return;
    }
    setSending(true);
    setLastResult(null);
    try {
      const res = await fetch(`${API}/admin/notifications/broadcast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: title.trim(),
          body: body.trim(),
          url: url.trim() || undefined,
        }),
      });
      const data = await res.json();
      setLastResult(data);
      if (res.ok) {
        if (data.sent > 0) {
          toast.success(`Sent to ${data.sent} device${data.sent === 1 ? '' : 's'} (${data.targeted_users} users)`);
        } else {
          toast.warning(data.message || data.error || 'No notifications sent');
        }
        setTitle(''); setBody(''); setUrl('');
        loadHistory();
      } else {
        toast.error(data.detail || 'Broadcast failed');
      }
    } catch (e) {
      toast.error('Network error');
    } finally {
      setSending(false);
    }
  };

  const fcmAvailable = status?.available === true;

  return (
    <Card className="p-5 glass-card" data-testid="admin-push-broadcast-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-blue-400" />
          <h2 className="text-lg font-bold">Push Notifications</h2>
          {status && (
            <span
              data-testid="fcm-status-badge"
              className={`text-[10px] px-2 py-0.5 rounded-full border uppercase tracking-wide ${
                fcmAvailable
                  ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
                  : 'bg-amber-500/15 text-amber-300 border-amber-500/40'
              }`}
            >
              {fcmAvailable ? 'FCM Connected' : 'Not Configured'}
            </span>
          )}
        </div>
        <Button size="sm" variant="outline" onClick={() => { loadStatus(); loadHistory(); }} data-testid="refresh-fcm-status">
          Refresh
        </Button>
      </div>

      {!fcmAvailable && status?.error && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 mb-4 flex gap-2 items-start">
          <AlertTriangle className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-amber-200">
            <p className="font-semibold mb-1">Server-side FCM not configured</p>
            <p className="text-amber-200/80 mb-2">{status.error}</p>
            <p className="text-amber-200/70">
              In Firebase Console → <em>Project Settings → Service Accounts → Generate new private key</em>,
              then paste the entire JSON into <code className="bg-black/30 px-1 py-0.5 rounded">FIREBASE_SERVICE_ACCOUNT_JSON</code> in <code className="bg-black/30 px-1 py-0.5 rounded">backend/.env</code> and restart the backend.
            </p>
          </div>
        </div>
      )}

      <div className="space-y-3 mb-4">
        <Input
          placeholder="Notification title (e.g. New feature released!)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          data-testid="broadcast-title-input"
          maxLength={120}
        />
        <Textarea
          placeholder="Message body — what should users see?"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          data-testid="broadcast-body-input"
          maxLength={500}
        />
        <Input
          placeholder="Click destination URL (optional, e.g. /forums)"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          data-testid="broadcast-url-input"
        />
        <div className="flex justify-end">
          <Button
            onClick={handleSend}
            disabled={sending || !fcmAvailable || !title.trim() || !body.trim()}
            data-testid="send-broadcast-btn"
          >
            {sending
              ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> Sending…</>
              : <><Send className="h-3.5 w-3.5 mr-2" /> Send to all users</>}
          </Button>
        </div>
      </div>

      {lastResult && (
        <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 p-3 mb-4 text-xs flex gap-2 items-start" data-testid="broadcast-result">
          <CheckCircle2 className="h-4 w-4 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-blue-200">
            <p>
              <strong>{lastResult.sent ?? 0}</strong> sent ·{' '}
              <strong>{lastResult.failed ?? 0}</strong> failed ·{' '}
              <strong>{lastResult.targeted_users ?? 0}</strong> unique users targeted
            </p>
            {lastResult.error && <p className="mt-1 text-amber-300">Error: {lastResult.error}</p>}
          </div>
        </div>
      )}

      {history.length > 0 && (
        <details className="rounded-lg border border-border bg-secondary/30 p-3" data-testid="broadcast-history">
          <summary className="cursor-pointer text-sm font-medium">Recent broadcasts ({history.length})</summary>
          <ul className="mt-3 space-y-2 text-xs max-h-64 overflow-y-auto">
            {history.map((b) => (
              <li key={b.broadcast_id} className="border-l-2 border-blue-500/40 pl-3">
                <p className="font-semibold truncate">{b.title}</p>
                <p className="text-muted-foreground truncate">{b.body}</p>
                <p className="text-[10px] text-muted-foreground mt-1">
                  {new Date(b.sent_at).toLocaleString()} ·{' '}
                  sent {b.sent}/{b.tokens_attempted} ·{' '}
                  {b.targeted_users} users
                </p>
              </li>
            ))}
          </ul>
        </details>
      )}
    </Card>
  );
}
